import React, { useState } from 'react';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { ShieldOff, ChevronDown } from 'lucide-react';

const TOGGLES = [
  {
    field: 'exclude_clock',
    title: 'Exclude from Clock-in / out',
    short: 'Clock-in/out',
    desc: 'Hidden from the clock kiosk, Staff PINs and the Live board. They cannot clock in or out.',
    testid: 'exclude-clock-switch',
  },
  {
    field: 'exclude_attendance',
    title: 'Exclude from Attendance',
    short: 'Attendance',
    desc: 'Hidden from the attendance matrix, clock reports, flagged punches, missed clock-outs and HR digests.',
    testid: 'exclude-attendance-switch',
  },
  {
    field: 'exclude_leave_salary',
    title: 'Exclude from Leave Salary',
    short: 'Leave Salary',
    desc: 'Excluded from the LOP (Loss of Pay) / leave-salary tracking report.',
    testid: 'exclude-leave-salary-switch',
  },
];

const TrackingExclusionsSection = ({ formData, handleChange }) => {
  const [open, setOpen] = useState(false);
  const active = TOGGLES.filter((t) => !!formData[t.field]);
  const allOn = active.length === TOGGLES.length;

  const setAll = (value) => {
    TOGGLES.forEach((t) => handleChange(t.field, value));
  };

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/60" data-testid="tracking-exclusions-section">
      {/* Compact one-line header — click to expand */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
        data-testid="tracking-exclusions-toggle"
      >
        <ShieldOff className="h-4 w-4 text-amber-600 shrink-0" />
        <span className="text-sm font-semibold text-amber-900 shrink-0">Tracking / Exclusions</span>
        {/* Status summary stays visible even when collapsed */}
        {active.length === 0 ? (
          <span className="text-xs text-amber-700/70">None</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {active.map((t) => (
              <Badge key={t.field} variant="outline" className="text-[10px] bg-white text-amber-700 border-amber-300">
                {t.short}
              </Badge>
            ))}
          </span>
        )}
        <ChevronDown className={`h-4 w-4 text-amber-600 ml-auto shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-3 pb-3 border-t border-amber-200/70">
          <p className="text-[11px] text-amber-700 mt-2 mb-2">
            Super-admin only. Freeze this employee from specific modules — useful for management / PROs who
            should not be tracked or penalised by attendance and payroll.
          </p>
          {/* Bulk master toggle — flip all exclusions with one click */}
          <div className="flex items-center justify-between gap-3 px-3 py-2 mb-2 rounded-md bg-amber-100/70 border border-amber-300">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-xs text-amber-900">Exclude from everything</p>
              <p className="text-[11px] text-amber-700/90">Toggle all three exclusions at once instead of one at a time.</p>
            </div>
            <Switch
              checked={allOn}
              onCheckedChange={(v) => setAll(v)}
              data-testid="exclude-all-switch"
            />
          </div>
          <div className="divide-y divide-amber-100 rounded-md bg-white border border-amber-200">
            {TOGGLES.map((t) => (
              <div key={t.field} className="flex items-center justify-between gap-3 px-3 py-2">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-xs">{t.title}</p>
                  <p className="text-[11px] text-muted-foreground truncate" title={t.desc}>{t.desc}</p>
                </div>
                <Switch
                  checked={!!formData[t.field]}
                  onCheckedChange={(v) => handleChange(t.field, v)}
                  data-testid={t.testid}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackingExclusionsSection;
